/** Brand theming: the token list, colour maths, the palette derived from one base colour, and `applyBrandTheme`,
 * which puts a brand's colours on the document. Defaults live in styles.css (per `data-brand`); a brand's saved
 * `theme` = { base?: hex, tokens?: { "--c-accent": hex, … } } overrides them: `base` re-derives the whole family,
 * `tokens` pin single colours on top. Legacy `{ primary, accent }` still works. */

export type ThemeGroup = "brand" | "neutrals" | "semantic" | "status";
export type TokenDef = { name: string; group: ThemeGroup; triplet: boolean };
export type BrandTheme = { base?: string; tokens?: Record<string, string>; primary?: string; accent?: string };

export const TOKENS: TokenDef[] = [
  { name: "--c-accent", group: "brand", triplet: true },
  { name: "--c-accent-strong", group: "brand", triplet: true },
  { name: "--c-accent-soft", group: "brand", triplet: true },
  { name: "--c-accent-ink", group: "brand", triplet: true },
  { name: "--logo-a", group: "brand", triplet: false },
  { name: "--logo-b", group: "brand", triplet: false },
  { name: "--c-ground", group: "neutrals", triplet: true },
  { name: "--c-surface", group: "neutrals", triplet: true },
  { name: "--c-surface-2", group: "neutrals", triplet: true },
  { name: "--c-line", group: "neutrals", triplet: true },
  { name: "--c-line-strong", group: "neutrals", triplet: true },
  { name: "--c-ink", group: "neutrals", triplet: true },
  { name: "--c-muted", group: "neutrals", triplet: true },
  { name: "--c-faint", group: "neutrals", triplet: true },
  { name: "--c-ok", group: "semantic", triplet: true },
  { name: "--c-ok-soft", group: "semantic", triplet: true },
  { name: "--c-warn", group: "semantic", triplet: true },
  { name: "--c-warn-soft", group: "semantic", triplet: true },
  { name: "--c-danger", group: "semantic", triplet: true },
  { name: "--c-danger-soft", group: "semantic", triplet: true },
  { name: "--c-info", group: "semantic", triplet: true },
  { name: "--c-info-soft", group: "semantic", triplet: true },
  { name: "--c-done", group: "status", triplet: true },
  { name: "--c-done-soft", group: "status", triplet: true },
  { name: "--c-cancel", group: "status", triplet: true },
];
/** Derived from the neutrals, never edited directly. */
const HATCH = ["--c-hatch-dark-a", "--c-hatch-dark-b", "--c-hatch-light-a", "--c-hatch-light-b"];
export const TOKEN_NAMES = new Set(TOKENS.map((t) => t.name));
const BY_NAME = new Map(TOKENS.map((t) => [t.name, t]));

// ---------- colour maths ----------
export const isHex = (v: string | undefined | null): v is string => !!v && /^#[0-9a-f]{6}$/i.test(v);
export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
export const rgbToHex = (r: number, g: number, b: number) => "#" + [r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("");
export const hexToTriplet = (hex: string) => hexToRgb(hex).join(" ");
export const tripletToHex = (t: string) => { const p = t.trim().split(/[\s,]+/).map(Number); return p.length === 3 && p.every((n) => !Number.isNaN(n)) ? rgbToHex(p[0], p[1], p[2]) : null; };
export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  return [h, s * 100, l * 100];
}
export function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360; s = Math.min(100, Math.max(0, s)) / 100; l = Math.min(100, Math.max(0, l)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}
export const hexToHsl = (hex: string) => rgbToHsl(...hexToRgb(hex));
/** WCAG contrast ratio between two hex colours. */
export function contrast(a: string, b: string): number {
  const lum = (hex: string) => { const [r, g, b] = hexToRgb(hex).map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; }); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };
  const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** One base colour → the brand family and neutrals that carry its hue (semantic colours are not derived). */
export function derive(base: string): Record<string, string> {
  const [h, s0] = hexToHsl(base);
  const s = Math.max(12, s0);
  const l = Math.min(52, Math.max(38, hexToHsl(base)[2]));
  const t = (hh: number, ss: number, ll: number) => hslToHex(hh, ss, ll);
  return {
    "--c-accent": t(h, s, l),
    "--c-accent-strong": t(h, s, l - 14),
    "--c-accent-soft": t(h, Math.max(25, s * 0.55), 94),
    "--c-accent-ink": t(h, Math.min(60, s * 0.5), 20),
    "--logo-a": t(h, Math.min(70, s * 0.9), 72),
    "--logo-b": t(h + 15, Math.min(45, s * 0.6), 45),
    "--c-ground": t(h, 30, 98),
    "--c-surface": "#ffffff",
    "--c-surface-2": t(h, 25, 95),
    "--c-line": t(h, 20, 90),
    "--c-line-strong": t(h, 18, 82),
    "--c-ink": t(h, 25, 12),
    "--c-muted": t(h, 10, 44),
    "--c-faint": t(h, 10, 63),
    "--c-done": t(h, 6, 50),
    "--c-done-soft": t(h, 15, 92),
    "--c-cancel": t(h, 8, 72),
  };
}
function hatches(tokens: Record<string, string>): Record<string, string> {
  const [h, s] = hexToHsl(tokens["--c-line"]);
  return { "--c-hatch-dark-a": hslToHex(h, s, 92), "--c-hatch-dark-b": hslToHex(h, s, 85), "--c-hatch-light-a": hslToHex(h, s, 96), "--c-hatch-light-b": hslToHex(h, s, 91) };
}

// ---------- document ----------
const root = () => document.documentElement;
/** The built-in colours of a brand (styles.css), read with every inline override lifted. */
export function readDefaults(slug: string): Record<string, string> {
  const el = root();
  const prevSlug = el.getAttribute("data-brand");
  const saved = [...TOKENS.map((t) => t.name), ...HATCH].map((n) => [n, el.style.getPropertyValue(n)] as const);
  saved.forEach(([n]) => el.style.removeProperty(n));
  el.setAttribute("data-brand", slug);
  const cs = getComputedStyle(el);
  const out: Record<string, string> = {};
  for (const t of TOKENS) {
    const raw = cs.getPropertyValue(t.name).trim();
    out[t.name] = (t.triplet ? tripletToHex(raw) : raw) || "#000000";
  }
  if (prevSlug) el.setAttribute("data-brand", prevSlug); else el.removeAttribute("data-brand");
  saved.forEach(([n, v]) => { if (v) el.style.setProperty(n, v); });
  return out;
}
/** Defaults → derived family (when `base` is set) → pinned tokens; legacy `primary` acts as a base. */
export function resolveTokens(slug: string, theme: BrandTheme | null | undefined): { tokens: Record<string, string>; defaults: Record<string, string>; derived: Record<string, string> } {
  const defaults = readDefaults(slug);
  const base = isHex(theme?.base) ? theme!.base! : isHex(theme?.primary) ? theme!.primary! : null;
  const derived = base ? derive(base) : {};
  const tokens = { ...defaults, ...derived };
  for (const [k, v] of Object.entries(theme?.tokens ?? {})) if (TOKEN_NAMES.has(k) && isHex(v)) tokens[k] = v.toLowerCase();
  return { tokens, defaults, derived };
}
/** Puts the brand on the document: `data-brand` selects the built-in set, inline properties carry the theme. */
export function applyBrandTheme(slug: string, theme?: BrandTheme | null) {
  const el = root();
  const { tokens, defaults } = resolveTokens(slug, theme);
  el.setAttribute("data-brand", slug);
  const custom = Object.keys(tokens).some((k) => tokens[k] !== defaults[k]);
  for (const t of TOKENS) {
    if (custom && tokens[t.name] !== defaults[t.name]) el.style.setProperty(t.name, t.triplet ? hexToTriplet(tokens[t.name]) : tokens[t.name]);
    else el.style.removeProperty(t.name);
  }
  const hatch = custom ? hatches(tokens) : null;
  for (const n of HATCH) { if (hatch) el.style.setProperty(n, hexToTriplet(hatch[n])); else el.style.removeProperty(n); }
}
/** Live preview of a draft theme (same as apply, but never clears a derived hatch while editing). */
export const previewTheme = applyBrandTheme;
export const tokenDef = (name: string) => BY_NAME.get(name);
